<template>
  <div class="container">
    <div class="row">
      <div class="col-12">
        <div class="mb-1">
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
              <li class="breadcrumb-item"><router-link :to="'/'">Home</router-link></li>
              <li class="breadcrumb-item active" aria-current="page">Inventory</li>
            </ol>
          </nav>
        </div>

        <h2 class="mb-3">📦 Inventory</h2>

        <PushAlerts />

        <div class="table-responsive mb-2">
          <table class="table align-middle">
            <thead>
              <tr>
                <th>Item</th>
                <th style="width: 8rem;">Count</th>
                <th style="width: 16rem;"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!items.length">
                <td colspan="3" class="text-muted">No inventory items yet. Add one below.</td>
              </tr>
              <tr v-for="item in items" :key="item._id">
                <td>
                  {{ item.name }}
                  <span v-if="item.orderedAt" class="badge bg-success ms-1">Ordered</span>
                </td>
                <td>
                  <input
                    v-if="editingId === item._id"
                    v-model.number="draftCount"
                    type="number"
                    min="0"
                    step="1"
                    class="form-control"
                    :disabled="saving"
                  >
                  <span v-else>{{ item.count }}</span>
                </td>
                <td class="text-end">
                  <template v-if="editingId === item._id">
                    <button
                      type="button"
                      class="btn btn-outline-success btn-sm me-1"
                      :disabled="saving"
                      @click="confirmEdit(item)"
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm"
                      :disabled="saving"
                      @click="cancelEdit"
                    >
                      Cancel
                    </button>
                  </template>
                  <template v-else>
                    <button
                      v-if="canMarkOrdered"
                      type="button"
                      class="btn btn-sm me-1"
                      :class="item.orderedAt ? 'btn-success' : 'btn-outline-warning'"
                      :disabled="saving"
                      @click="setOrdered(item, !item.orderedAt)"
                    >
                      Ordered
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-primary btn-sm"
                      :disabled="saving"
                      @click="startEdit(item)"
                    >
                      Edit
                    </button>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mb-4">
          <button
            v-if="!showAddForm"
            type="button"
            class="btn btn-outline-secondary btn-sm"
            @click="openAddForm"
          >
            Add item
          </button>
          <form v-else class="row g-2 align-items-end" @submit.prevent="addItem">
            <div class="col-12 col-sm-6">
              <label class="form-label" for="new-item-name">Name</label>
              <input
                id="new-item-name"
                v-model="newItem.name"
                type="text"
                class="form-control"
                required
                :disabled="adding"
              >
            </div>
            <div class="col-6 col-sm-2">
              <label class="form-label" for="new-item-count">Count</label>
              <input
                id="new-item-count"
                v-model.number="newItem.count"
                type="number"
                min="0"
                step="1"
                class="form-control"
                required
                :disabled="adding"
              >
            </div>
            <div class="col-6 col-sm-4">
              <button type="submit" class="btn btn-outline-success btn-sm me-1" :disabled="adding">
                {{ adding ? 'Adding...' : 'Add' }}
              </button>
              <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="adding" @click="closeAddForm">
                Cancel
              </button>
            </div>
          </form>
        </div>

        <h3 class="mb-3">Change log</h3>

        <div v-if="!changes.length && !changesLoading" class="text-muted mb-3">
          No inventory changes yet.
        </div>

        <div v-for="change in changes" :key="change._id" class="card px-3 py-1 mb-3">
          <span>
            <strong>{{ changeSummary(change) }}</strong>
          </span>
          <div class="d-flex flex-row small-footer">
            <span class="me-auto">
              <b-icon icon="person"></b-icon>&nbsp;
              {{ change.authorName }}
            </span>
            <span>{{ change.createdAt | moment("from", "now") }}</span>
          </div>
        </div>

        <div v-if="pagination.hasMore" class="text-center mt-4">
          <button
            type="button"
            class="btn btn-outline-primary"
            :disabled="changesLoading"
            @click="loadMore"
          >
            <span v-if="changesLoading">Loading...</span>
            <span v-else>Load More ({{ pagination.total - (pagination.offset + pagination.limit) }} remaining)</span>
          </button>
        </div>

        <div v-else-if="changes.length > 0" class="text-center mt-4 text-muted">
          <small>All {{ pagination.total }} inventory changes loaded</small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PushAlerts from './PushAlerts.vue';

export default {
  name: 'Inventory',
  components: {
    PushAlerts,
  },
  data: () => ({
    items: [],
    changes: [],
    pagination: {
      limit: 20,
      offset: 0,
      total: 0,
      hasMore: false,
    },
    newItem: {
      name: '',
      count: 0,
    },
    showAddForm: false,
    editingId: null,
    draftCount: 0,
    adding: false,
    saving: false,
    changesLoading: false,
    superuser: false,
    inventoryAlerts: false,
  }),
  computed: {
    canMarkOrdered() {
      return this.superuser || this.inventoryAlerts;
    },
  },
  async mounted() {
    await Promise.all([
      this.loadItems(),
      this.loadChanges(),
      this.loadPermissions(),
    ]);
  },
  methods: {
    isValidCount(value) {
      return Number.isInteger(value) && value >= 0;
    },
    changeSummary(change) {
      if (change.action === 'created') {
        return `Added ${change.itemName} with count ${change.newCount}`;
      }
      if (change.action === 'ordered') {
        return `${change.authorName} marked ${change.itemName} as ordered`;
      }
      if (change.action === 'unordered') {
        return `${change.authorName} unmarked ${change.itemName} as ordered`;
      }
      return `${change.itemName}: ${change.previousCount} → ${change.newCount}`;
    },
    async loadItems() {
      const { data } = await this.$api.get('/inventory');
      this.items = data.data;
    },
    async loadPermissions() {
      try {
        const [{ data: me }, { data: pushStatus }] = await Promise.all([
          this.$api.get('/me'),
          this.$api.get('/push/status', { timeout: 10000 }),
        ]);
        this.superuser = me.superuser === true;
        this.inventoryAlerts = pushStatus.inventoryAlerts === true;
      } catch (error) {
        console.error('Error loading inventory permissions:', error);
      }
    },
    async loadChanges(reset = true) {
      this.changesLoading = true;

      try {
        const params = {
          limit: this.pagination.limit,
          offset: reset ? 0 : this.pagination.offset,
        };

        const { data } = await this.$api.get('/inventory/changes', { params });

        if (reset) {
          this.changes = data.data;
          this.pagination = data.pagination;
        } else {
          this.changes = [...this.changes, ...data.data];
          this.pagination = data.pagination;
        }
      } catch (error) {
        console.error('Error loading inventory changes:', error);
      } finally {
        this.changesLoading = false;
      }
    },
    async loadMore() {
      this.pagination.offset = this.changes.length;
      await this.loadChanges(false);
    },
    openAddForm() {
      this.showAddForm = true;
    },
    closeAddForm() {
      this.showAddForm = false;
      this.newItem = { name: '', count: 0 };
    },
    startEdit(item) {
      this.editingId = item._id;
      this.draftCount = item.count;
    },
    cancelEdit() {
      this.editingId = null;
      this.draftCount = 0;
    },
    async confirmEdit(item) {
      if (!this.isValidCount(this.draftCount)) {
        window.alert('Count must be a non-negative integer');
        return;
      }

      if (this.draftCount === item.count) {
        this.cancelEdit();
        return;
      }

      this.saving = true;
      try {
        await this.$api.patch(`/inventory/${item._id}`, { count: this.draftCount });
        this.cancelEdit();
        await this.loadItems();
        await this.loadChanges(true);
      } catch (error) {
        console.error('Error updating inventory item:', error);
      } finally {
        this.saving = false;
      }
    },
    async setOrdered(item, ordered) {
      this.saving = true;
      try {
        await this.$api.patch(`/inventory/${item._id}`, { ordered: !!ordered });
        await this.loadItems();
        await this.loadChanges(true);
      } catch (error) {
        console.error('Error updating ordered state:', error);
      } finally {
        this.saving = false;
      }
    },
    async addItem() {
      const name = this.newItem.name.trim();
      const count = this.newItem.count;

      if (!name) {
        window.alert('Name is required');
        return;
      }
      if (!this.isValidCount(count)) {
        window.alert('Count must be a non-negative integer');
        return;
      }

      this.adding = true;
      try {
        await this.$api.post('/inventory', { name, count });
        this.closeAddForm();
        await this.loadItems();
        await this.loadChanges(true);
      } catch (error) {
        console.error('Error adding inventory item:', error);
      } finally {
        this.adding = false;
      }
    },
  },
}
</script>

<style scoped>
.small-footer {
  font-size: 0.75em;
}
</style>
